import { RoleFeatureFlag, FeatureDefinition } from '../types/roleFeature';
import { Role } from '../types/user';

// Define the features locally to avoid circular dependency
const availableFeatures: FeatureDefinition[] = [
  { 
    key: 'dashboard', 
    name: 'Dashboard', 
    description: 'Main dashboard page',
    defaultEnabledForRoles: ['admin', 'user']
  },
  { 
    key: 'upload_csv', 
    name: 'Upload CSV', 
    description: 'Upload CSV files',
    defaultEnabledForRoles: ['admin', 'user']
  },
  { 
    key: 'upload_csv_questions', 
    name: 'Upload CSV with Questions', 
    description: 'Upload CSV files with questionnaire',
    defaultEnabledForRoles: ['admin', 'user']
  },
  { 
    key: 'csv_demo', 
    name: 'CSV Demo', 
    description: 'CSV demonstration page',
    defaultEnabledForRoles: ['admin', 'user']
  },
  { 
    key: 'brand_maintenance', 
    name: 'Brand Maintenance', 
    description: 'Manage brands',
    defaultEnabledForRoles: ['admin', 'user']
  },
  { 
    key: 'market_maintenance', 
    name: 'Market Maintenance', 
    description: 'Manage markets',
    defaultEnabledForRoles: ['admin', 'user']
  },
  { 
    key: 'dealer_group_maintenance', 
    name: 'Dealer Group Maintenance', 
    description: 'Manage dealer groups',
    defaultEnabledForRoles: ['admin', 'user']
  },
  { 
    key: 'map_data', 
    name: 'Map Data', 
    description: 'Data mapping functionality',
    defaultEnabledForRoles: ['admin', 'user']
  },
  { 
    key: 'financial_data', 
    name: 'View Data', 
    description: 'View financial data',
    defaultEnabledForRoles: ['admin', 'user']
  },
  { 
    key: 'analytics', 
    name: 'Analytics', 
    description: 'Analytics dashboard',
    defaultEnabledForRoles: ['admin', 'user']
  },
  { 
    key: 'visuai_data_viewer', 
    name: 'VisuAI Data Viewer', 
    description: 'AI-powered data visualization',
    defaultEnabledForRoles: ['admin', 'user']
  },
  { 
    key: 'user_maintenance', 
    name: 'User Maintenance', 
    description: 'Manage users',
    defaultEnabledForRoles: ['admin']
  },
  { 
    key: 'group_user_maintenance', 
    name: 'Group User Management', 
    description: 'Manage group users',
    defaultEnabledForRoles: ['admin', 'group_admin']
  },
  { 
    key: 'role_maintenance', 
    name: 'Role Maintenance', 
    description: 'Manage role permissions',
    defaultEnabledForRoles: ['admin']
  }
];

// Helper to generate default feature flags for a role
const generateDefaultFlags = (roleId: string): RoleFeatureFlag[] => {
  return availableFeatures.map((feature: FeatureDefinition) => ({
    roleId,
    featureKey: feature.key,
    enabled: feature.defaultEnabledForRoles.includes(roleId as Role),
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  }));
};

// In-memory storage for mock data
const mockStorage: Record<string, RoleFeatureFlag[]> = {
  admin: generateDefaultFlags('admin'),
  user: generateDefaultFlags('user'),
  group_admin: generateDefaultFlags('group_admin')
};

export const mockRoleFeatureService = {
  // Get all feature flags
  getAllFeatureFlags: async (): Promise<RoleFeatureFlag[]> => {
    console.log('[MOCK] Getting all feature flags');
    // Combine all role flags
    const allFlags: RoleFeatureFlag[] = [];
    Object.values(mockStorage).forEach(flags => {
      allFlags.push(...flags);
    });
    return allFlags;
  },
  
  // Get feature flags for a specific role
  getRoleFeatures: async (roleId: string): Promise<RoleFeatureFlag[]> => {
    console.log(`[MOCK] Getting feature flags for role ${roleId}`);
    
    // If the role doesn't exist yet, create default flags
    if (!mockStorage[roleId]) {
      mockStorage[roleId] = generateDefaultFlags(roleId);
    }
    
    return mockStorage[roleId];
  },
  
  // Update a feature flag
  updateFeatureFlag: async (roleId: string, featureKey: string, enabled: boolean): Promise<RoleFeatureFlag> => {
    console.log(`[MOCK] Updating feature flag ${featureKey} for role ${roleId} to ${enabled}`);
    
    // If the role doesn't exist yet, create default flags
    if (!mockStorage[roleId]) {
      mockStorage[roleId] = generateDefaultFlags(roleId);
    }
    
    // Find and update the flag
    const flagIndex = mockStorage[roleId].findIndex(flag => flag.featureKey === featureKey);
    
    if (flagIndex >= 0) {
      mockStorage[roleId][flagIndex].enabled = enabled;
      mockStorage[roleId][flagIndex].updatedAt = new Date().toISOString();
      return mockStorage[roleId][flagIndex];
    }
    
    // If the flag doesn't exist, create it
    const newFlag: RoleFeatureFlag = {
      roleId,
      featureKey,
      enabled,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    mockStorage[roleId].push(newFlag);
    return newFlag;
  }
};

export default mockRoleFeatureService; 